import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Silent, Tuple } from "@/__main__/data-model.mjs";
// import { RANK_SYMBOLS } from "@/game-lol/constants/constants-ranks.mjs";
// import { ROLE_SYMBOLS } from "@/game-lol/utils/roles.mjs";
import QueueSymbol from "@/game-lol/utils/symbol-queue.mjs";
import { RankSymbol } from "@/game-lol/utils/symbol-rank.mjs";
import RoleSymbol from "@/game-lol/utils/symbol-role.mjs";

const COLUMN_COUNT = 23;
const COLUMNS = Array.from({ length: COLUMN_COUNT }, () => String);

const buildModel = Tuple(COLUMNS) satisfies ModelDefinition;

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const MatchupResult = {
  patch: String,
  queue_id: QueueSymbol,
  champion_id: Number,
  championId: Number,
  individual_position: RoleSymbol,
  matchup_champion_id: Number,
  tier: RankSymbol,
  region: String,
  games: Number,
  wins: Number,
  num_lane_wins: Number,
  num_lanes: Number,
  win_rate: Number,
  lane_win_rate: Number,
  champion_num_games: Number,
  champion_win_rate: Number,
  enemy_champion_num_games: Number,
  enemy_champion_win_rate: Number,
  pick_rate: Number,
  matchup_score: Number,
  lane_matchup_score: Number,
  champion_matchup_tier: Number,
  matchup_tier: Number,
  filters: String,
} satisfies ModelDefinition;

const MatchupsModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Silent(Number),
          dataArray: [buildModel],
          rowCount: Silent(Number),
          rowOffset: Silent(Number),
        },
      },
    },
  },
}).transform([MatchupResult], (data) => {
  const {
    data: { executeDatabricksQuery },
  } = data;

  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const champions = list.map((c) => {
    const result = {
      patch: "",
      queue_id: null,
      champion_id: 0,
      championId: 0,
      individual_position: null,
      matchup_champion_id: 0,
      tier: null,
      region: "",
      games: 0,
      wins: 0,
      num_lane_wins: 0,
      num_lanes: 0,
      win_rate: 0,
      lane_win_rate: 0,
      champion_num_games: 0,
      champion_win_rate: 0,
      enemy_champion_num_games: 0,
      enemy_champion_win_rate: 0,
      pick_rate: 0,
      matchup_score: 0,
      lane_matchup_score: 0,
      champion_matchup_tier: 0,
      matchup_tier: 0,
      filters: "",
    };
    for (const column of columns) {
      const value: string = (c[column.position] || "") as unknown as string;
      result[column.name] = castType(column.typeText, value);
    }

    result.championId = result.champion_id;

    return result;
  });

  return champions;
});

export default MatchupsModel;

export type ChampionMatchups = FromModel<typeof MatchupsModel>;

function castType(typeText: string, value: string) {
  switch (typeText) {
    case "BOOLEAN":
      return value === "true";
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
    case "INT":
      return value ? Number(value) : 0;
    case "STRING":
    case undefined:
    default:
      return value ?? "";
  }
}
